import { useState } from "react";
import { Trash2 } from "lucide-react";
import {
  Button,
  Hint,
  Input,
  Notice,
  SectionHeading,
} from "../../../components/ui";
import { authService } from "../../auth";

const CONFIRM_WORD = "delete";

/** Two steps: a plain button, then a typed confirmation before anything runs. */
export const DeleteAccountSection = ({ isSubscribed }: { isSubscribed: boolean }) => {
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  const cancel = () => {
    setConfirming(false);
    setTyped("");
    setError("");
  };

  const deleteAccount = async () => {
    setDeleting(true);
    setError("");
    const message = await authService.deleteAccount();
    // On success the session ends and the app leaves this screen.
    if (message) {
      setError(message);
      setDeleting(false);
    }
  };

  return (
    <section>
      <SectionHeading>Delete account</SectionHeading>
      {confirming ? (
        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (!deleting && typed.trim().toLowerCase() === CONFIRM_WORD) {
              void deleteAccount();
            }
          }}
        >
          <Notice tone="warning">
            This permanently deletes your account, your website, its pages and
            your uploaded files, and takes your site offline.
            {isSubscribed && " Your subscription is cancelled immediately."} It
            can’t be undone.
          </Notice>
          <label className="block text-sm" htmlFor="delete-account-confirm">
            Type <strong>{CONFIRM_WORD}</strong> to confirm
          </label>
          <Input
            autoCapitalize="none"
            autoComplete="off"
            autoCorrect="off"
            disabled={deleting}
            id="delete-account-confirm"
            onChange={(event) => setTyped(event.target.value)}
            spellCheck={false}
            value={typed}
          />
          <div className="flex items-center gap-3">
            <Button
              disabled={deleting || typed.trim().toLowerCase() !== CONFIRM_WORD}
              size="sm"
              type="submit"
              variant="danger"
            >
              <Trash2 size={14} />
              {deleting ? "Deleting…" : "Delete my account"}
            </Button>
            <Button disabled={deleting} onClick={cancel} size="sm" variant="ghost">
              Cancel
            </Button>
          </div>
          {error && <Notice>{error}</Notice>}
        </form>
      ) : (
        <>
          <Hint className="mb-3">
            Permanently remove your account and everything in it.
          </Hint>
          <Button
            destructive
            onClick={() => setConfirming(true)}
            size="sm"
            variant="secondary"
          >
            <Trash2 size={14} />
            Delete account…
          </Button>
        </>
      )}
    </section>
  );
};
